import React, { useState, useEffect } from "react";
import Sidebar from "./../components/sidebar";
import { Card, Row, Col, Statistic, Typography, Space, Button, Modal, Input, message } from "antd";
import axios from "axios";
import portadacirp from "./../assets/video/portada.png";
import logohorcirp from "./../assets/img/lodohorcirp.jpg";
import { PlayCircleFilled, UserOutlined, EditOutlined } from "@ant-design/icons";
import "./../assets/css/inicio.css";
import { Link } from "react-router-dom";
import WhatsAppButton from "../components/WhatsAppButton";

const { Title, Text } = Typography;
const url = "https://api.mrcirp.com/public/usuarioscantidad";
const videoApiUrl = "https://api.mrcirp.com/public/videos";

const Inicio = () => {
  const [data, setData] = useState({ cantidad_usuarios: 0 });
  const [videoEnded, setVideoEnded] = useState(false);
  const [users, setUsers] = useState({});
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentVideo, setCurrentVideo] = useState("");
  const [loadingVideo, setLoadingVideo] = useState(true);
  const [videoUrl, setVideoUrl] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [videoType, setVideoType] = useState(""); // 'mp4', 'youtube', 'vimeo'

  useEffect(() => {
    const loginUserJSON = window.localStorage.getItem("loginUser");
    if (loginUserJSON) {
      const user = JSON.parse(loginUserJSON);
      setUsers(user);
    }
  }, []);

  const peticionGet = async () => {
    try {
      const response = await axios.get(url);
      setData(response.data[0] || { cantidad_usuarios: 0 });
    } catch (error) {
      console.error("Error fetching user count:", error);
      setData({ cantidad_usuarios: 0 });
    }
  };

  const detectVideoType = (url) => {
    if (!url) return null;
    
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      return 'youtube';
    } else if (url.includes('vimeo.com')) {
      return 'vimeo';
    } else if (url.endsWith('.mp4') || url.includes('mp4')) {
      return 'mp4';
    }
    return null;
  };

  const getYoutubeEmbedUrl = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    const videoId = (match && match[2].length === 11) ? match[2] : null;
    return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
  };

  const getVimeoEmbedUrl = (url) => {
    const regExp = /https?:\/\/(?:www\.|player\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|album\/(\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/;
    const match = url.match(regExp);
    const videoId = match ? match[3] : null;
    return `https://player.vimeo.com/video/${videoId}?autoplay=1`;
  };

  const fetchVideo = async () => {
    try {
      const response = await axios.get(videoApiUrl);
      if (response.data && response.data[0] && response.data[0].urlvideo) {
        const videoUrl = response.data[0].urlvideo;
        const type = detectVideoType(videoUrl);
        setVideoType(type);
        
        if (type === 'mp4') {
          setCurrentVideo(`${videoUrl}?t=${Date.now()}`);
        } else {
          setCurrentVideo(videoUrl);
        }
        
        setVideoUrl(videoUrl);
      }
    } catch (error) {
      console.error("Error fetching video:", error);
      message.error("Error al cargar el video");
    } finally {
      setLoadingVideo(false);
    }
  };

  useEffect(() => {
    peticionGet();
    fetchVideo();
  }, []);

  const handleVideoEnded = () => {
    setVideoEnded(true);
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    if (currentVideo) {
      setVideoUrl(currentVideo.split('?')[0]);
    }
  };

  const handleUpdateVideo = async () => {
    if (!videoUrl) {
      message.error('Por favor ingresa una URL válida');
      return;
    }

    const type = detectVideoType(videoUrl);
    if (!type) {
      message.error('URL de video no válida. Debe ser MP4, YouTube o Vimeo');
      return;
    }

    setIsUpdating(true);

    try {
      const cleanUrl = videoUrl.split('?')[0];
      const token = users.token || '';
      const videoId = 1;
      
      const response = await axios.put(`${videoApiUrl}/${videoId}`, {
        urlvideo: cleanUrl
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        }
      });
      
      if (response.data) {
        message.success('Video actualizado exitosamente');
        setVideoType(type);
        
        if (type === 'mp4') {
          setCurrentVideo(`${cleanUrl}?t=${Date.now()}`);
        } else {
          setCurrentVideo(cleanUrl);
        }
        
        setVideoUrl(cleanUrl);
      } else {
        message.error('Error al actualizar el video');
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = error.response?.data?.message || 
                         error.response?.data?.error || 
                         'Error al actualizar el video';
      message.error(errorMessage);
    } finally {
      setIsUpdating(false);
      setIsModalVisible(false);
    }
  };

  const renderVideoPlayer = () => {
    if (!currentVideo) return null;

    switch (videoType) {
      case 'youtube':
        return (
          <iframe
            width="100%"
            height="400"
            src={getYoutubeEmbedUrl(currentVideo)}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="YouTube video player"
          />
        );
      case 'vimeo':
        return (
          <iframe
            src={getVimeoEmbedUrl(currentVideo)}
            width="100%"
            height="400"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            title="Vimeo video player"
          />
        );
      case 'mp4':
        return (
          <video
            width="100%"
            controls
            autoPlay
            poster={portadacirp}
            onEnded={handleVideoEnded}
            key={currentVideo}
          >
            <source src={currentVideo} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        );
      default:
        return <Text>Formato de video no soportado</Text>;
    }
  };

  return (
    <div className="modern-layout">
      <Sidebar>
        <div className="welcome-section">
          <Title level={3} className="welcome-title">
            Bienvenido {users.username || "Invitado"}
          </Title>
          <Text type="secondary" className="welcome-subtitle">
            Constructor de ingresos residuales perpetuos
          </Text>
        </div>

        <Row gutter={[24, 24]} className="content-row">
          <Col xs={24} md={16}>
            <Card
              className="video-card"
              cover={
                !videoEnded ? (
                  <div className="video-container">
                    {!loadingVideo && renderVideoPlayer()}
                  </div>
                ) : (
                  <div className="logo-container">
                    <img
                      src={logohorcirp}
                      alt="Logo CIRP"
                      className="responsive-logo"
                    />
                  </div>
                )
              }
            >
              <div className="card-actions">
                <PlayCircleFilled style={{ fontSize: "18px", color: "#1890ff" }} />
                <Text>Presentación Corporativa</Text>
              </div>
            </Card>
          </Col>

          <Col xs={24} md={8}>
            <Card className="stats-card" hoverable>
              <Statistic
                title={
                  <Space>
                    <UserOutlined />
                    <span>Usuarios registrados</span>
                  </Space>
                }
                value={data.cantidad_usuarios}
                valueStyle={{ color: "#3f8600" }}
                className="statistic-value"
              />
              <div className="progress-container">
                <div
                  className="progress-bar"
                  style={{ width: `${Math.min(data.cantidad_usuarios || 0, 100)}%` }}
                ></div>
              </div>
              <Text type="secondary" className="stats-description">
                Comunidad activa en nuestra plataforma
              </Text>
            </Card>

            <Card className="quick-actions-card" hoverable>
              <Title level={5} className="quick-actions-title">
                Acciones rápidas
              </Title>
              <div className="quick-actions">
                <div 
                  className="action-item"
                  onClick={showModal}
                  style={{ cursor: 'pointer' }}
                >
                  <EditOutlined style={{ marginRight: 8 }} />
                  Editar Video de inicio
                </div>
                <Link to="/crearcursos/" className="action-item">
                  Crear Cursos
                </Link>
                <Link to="/leadstodos/" className="action-item">
                  Gestión de Leads
                </Link>
                <Link to="/reportes" className="action-item">
                  Reportes
                </Link>
              </div>
            </Card>
          </Col>
        </Row>
      </Sidebar>

      <Modal
        title="Editar Video Corporativo"
        visible={isModalVisible}
        onCancel={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancelar
          </Button>,
          <Button
            key="update"
            type="primary"
            onClick={handleUpdateVideo}
            loading={isUpdating}
            disabled={!videoUrl}
          >
            {isUpdating ? 'Actualizando...' : 'Actualizar Video'}
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 16 }}>
          <Text strong>URL del Video:</Text>
          <Input 
            placeholder="Ingresa la URL del video (MP4, YouTube o Vimeo)" 
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            style={{ marginTop: 8 }}
          />
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            Ejemplos: 
            <br />- MP4: https://ejemplo.com/video.mp4
            <br />- YouTube: https://youtu.be/VIDEO_ID o https://www.youtube.com/watch?v=VIDEO_ID
            <br />- Vimeo: https://vimeo.com/VIDEO_ID
          </Text>
        </div>
      </Modal>

      <Footer />
      
      {/* Componentes flotantes */}
      <WhatsAppButton 
        phoneNumber="04267895263" 
        message="Hola, me interesa conocer más sobre CIRP" 
      />
    </div>
  );
};

const Footer = () => (
  <footer className="modern-footer">
    <Text type="secondary">
      Copyright© 2024 - Benjamin Romero Corporación S.A. SJO Costa Rica - Todos
      los derechos reservados Ley 6683 Privacy Policy
    </Text>
  </footer>
);

export default Inicio;